import React, { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "react-hot-toast";
import {
    useGetCTAPopupsQuery,
    useUpdateCTAPopupMutation,
    CTA
} from "../../store/slices/apiSlice";
import { Card } from "../../components/ui/Card";
import { PageHeader } from "../../components/ui/PageHeader";
import { Button } from "../../components/ui/Button";
import {
    ArrowLeft,
    Save,
    Upload,
    Eye,
    X,
    Image as ImageIcon,
    Video,
    Trash2,
    Globe,
    ChevronDown,
    Search
} from 'lucide-react';
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";

// ── World Countries List ────────────────────────────────────────────────────────
const WORLD_COUNTRIES = [
    { code: 'AF', name: 'Afghanistan' }, { code: 'AL', name: 'Albania' }, { code: 'DZ', name: 'Algeria' },
    { code: 'AD', name: 'Andorra' }, { code: 'AO', name: 'Angola' }, { code: 'AG', name: 'Antigua & Barbuda' },
    { code: 'AR', name: 'Argentina' }, { code: 'AM', name: 'Armenia' }, { code: 'AU', name: 'Australia' },
    { code: 'AT', name: 'Austria' }, { code: 'AZ', name: 'Azerbaijan' }, { code: 'BS', name: 'Bahamas' },
    { code: 'BH', name: 'Bahrain' }, { code: 'BD', name: 'Bangladesh' }, { code: 'BB', name: 'Barbados' },
    { code: 'BY', name: 'Belarus' }, { code: 'BE', name: 'Belgium' }, { code: 'BZ', name: 'Belize' },
    { code: 'BJ', name: 'Benin' }, { code: 'BT', name: 'Bhutan' }, { code: 'BO', name: 'Bolivia' },
    { code: 'BA', name: 'Bosnia & Herzegovina' }, { code: 'BW', name: 'Botswana' }, { code: 'BR', name: 'Brazil' },
    { code: 'BN', name: 'Brunei' }, { code: 'BG', name: 'Bulgaria' }, { code: 'BF', name: 'Burkina Faso' },
    { code: 'BI', name: 'Burundi' }, { code: 'CV', name: 'Cabo Verde' }, { code: 'KH', name: 'Cambodia' },
    { code: 'CM', name: 'Cameroon' }, { code: 'CA', name: 'Canada' }, { code: 'CF', name: 'Central African Republic' },
    { code: 'TD', name: 'Chad' }, { code: 'CL', name: 'Chile' }, { code: 'CN', name: 'China' },
    { code: 'CO', name: 'Colombia' }, { code: 'KM', name: 'Comoros' }, { code: 'CG', name: 'Congo' },
    { code: 'CD', name: 'Congo (DR)' }, { code: 'CR', name: 'Costa Rica' }, { code: 'CI', name: "Côte d'Ivoire" },
    { code: 'HR', name: 'Croatia' }, { code: 'CU', name: 'Cuba' }, { code: 'CY', name: 'Cyprus' },
    { code: 'CZ', name: 'Czech Republic' }, { code: 'DK', name: 'Denmark' }, { code: 'DJ', name: 'Djibouti' },
    { code: 'DM', name: 'Dominica' }, { code: 'DO', name: 'Dominican Republic' }, { code: 'EC', name: 'Ecuador' },
    { code: 'EG', name: 'Egypt' }, { code: 'SV', name: 'El Salvador' }, { code: 'GQ', name: 'Equatorial Guinea' },
    { code: 'ER', name: 'Eritrea' }, { code: 'EE', name: 'Estonia' }, { code: 'SZ', name: 'Eswatini' },
    { code: 'ET', name: 'Ethiopia' }, { code: 'FJ', name: 'Fiji' }, { code: 'FI', name: 'Finland' },
    { code: 'FR', name: 'France' }, { code: 'GA', name: 'Gabon' }, { code: 'GM', name: 'Gambia' },
    { code: 'GE', name: 'Georgia' }, { code: 'DE', name: 'Germany' }, { code: 'GH', name: 'Ghana' },
    { code: 'GR', name: 'Greece' }, { code: 'GD', name: 'Grenada' }, { code: 'GT', name: 'Guatemala' },
    { code: 'GN', name: 'Guinea' }, { code: 'GW', name: 'Guinea-Bissau' }, { code: 'GY', name: 'Guyana' },
    { code: 'HT', name: 'Haiti' }, { code: 'HN', name: 'Honduras' }, { code: 'HU', name: 'Hungary' },
    { code: 'IS', name: 'Iceland' }, { code: 'IN', name: 'India' }, { code: 'ID', name: 'Indonesia' },
    { code: 'IR', name: 'Iran' }, { code: 'IQ', name: 'Iraq' }, { code: 'IE', name: 'Ireland' },
    { code: 'IL', name: 'Israel' }, { code: 'IT', name: 'Italy' }, { code: 'JM', name: 'Jamaica' },
    { code: 'JP', name: 'Japan' }, { code: 'JO', name: 'Jordan' }, { code: 'KZ', name: 'Kazakhstan' },
    { code: 'KE', name: 'Kenya' }, { code: 'KI', name: 'Kiribati' }, { code: 'KP', name: 'Korea (North)' },
    { code: 'KR', name: 'Korea (South)' }, { code: 'KW', name: 'Kuwait' }, { code: 'KG', name: 'Kyrgyzstan' },
    { code: 'LA', name: 'Laos' }, { code: 'LV', name: 'Latvia' }, { code: 'LB', name: 'Lebanon' },
    { code: 'LS', name: 'Lesotho' }, { code: 'LR', name: 'Liberia' }, { code: 'LY', name: 'Libya' },
    { code: 'LI', name: 'Liechtenstein' }, { code: 'LT', name: 'Lithuania' }, { code: 'LU', name: 'Luxembourg' },
    { code: 'MG', name: 'Madagascar' }, { code: 'MW', name: 'Malawi' }, { code: 'MY', name: 'Malaysia' },
    { code: 'MV', name: 'Maldives' }, { code: 'ML', name: 'Mali' }, { code: 'MT', name: 'Malta' },
    { code: 'MH', name: 'Marshall Islands' }, { code: 'MR', name: 'Mauritania' }, { code: 'MU', name: 'Mauritius' },
    { code: 'MX', name: 'Mexico' }, { code: 'FM', name: 'Micronesia' }, { code: 'MD', name: 'Moldova' },
    { code: 'MC', name: 'Monaco' }, { code: 'MN', name: 'Mongolia' }, { code: 'ME', name: 'Montenegro' },
    { code: 'MA', name: 'Morocco' }, { code: 'MZ', name: 'Mozambique' }, { code: 'MM', name: 'Myanmar' },
    { code: 'NA', name: 'Namibia' }, { code: 'NR', name: 'Nauru' }, { code: 'NP', name: 'Nepal' },
    { code: 'NL', name: 'Netherlands' }, { code: 'NZ', name: 'New Zealand' }, { code: 'NI', name: 'Nicaragua' },
    { code: 'NE', name: 'Niger' }, { code: 'NG', name: 'Nigeria' }, { code: 'MK', name: 'North Macedonia' },
    { code: 'NO', name: 'Norway' }, { code: 'OM', name: 'Oman' }, { code: 'PK', name: 'Pakistan' },
    { code: 'PW', name: 'Palau' }, { code: 'PA', name: 'Panama' }, { code: 'PG', name: 'Papua New Guinea' },
    { code: 'PY', name: 'Paraguay' }, { code: 'PE', name: 'Peru' }, { code: 'PH', name: 'Philippines' },
    { code: 'PL', name: 'Poland' }, { code: 'PT', name: 'Portugal' }, { code: 'QA', name: 'Qatar' },
    { code: 'RO', name: 'Romania' }, { code: 'RU', name: 'Russia' }, { code: 'RW', name: 'Rwanda' },
    { code: 'KN', name: 'Saint Kitts & Nevis' }, { code: 'LC', name: 'Saint Lucia' },
    { code: 'VC', name: 'Saint Vincent & the Grenadines' }, { code: 'WS', name: 'Samoa' },
    { code: 'SM', name: 'San Marino' }, { code: 'ST', name: 'São Tomé & Príncipe' },
    { code: 'SA', name: 'Saudi Arabia' }, { code: 'SN', name: 'Senegal' }, { code: 'RS', name: 'Serbia' },
    { code: 'SC', name: 'Seychelles' }, { code: 'SL', name: 'Sierra Leone' }, { code: 'SG', name: 'Singapore' },
    { code: 'SK', name: 'Slovakia' }, { code: 'SI', name: 'Slovenia' }, { code: 'SB', name: 'Solomon Islands' },
    { code: 'SO', name: 'Somalia' }, { code: 'ZA', name: 'South Africa' }, { code: 'SS', name: 'South Sudan' },
    { code: 'ES', name: 'Spain' }, { code: 'LK', name: 'Sri Lanka' }, { code: 'SD', name: 'Sudan' },
    { code: 'SR', name: 'Suriname' }, { code: 'SE', name: 'Sweden' }, { code: 'CH', name: 'Switzerland' },
    { code: 'SY', name: 'Syria' }, { code: 'TW', name: 'Taiwan' }, { code: 'TJ', name: 'Tajikistan' },
    { code: 'TZ', name: 'Tanzania' }, { code: 'TH', name: 'Thailand' }, { code: 'TL', name: 'Timor-Leste' },
    { code: 'TG', name: 'Togo' }, { code: 'TO', name: 'Tonga' }, { code: 'TT', name: 'Trinidad & Tobago' },
    { code: 'TN', name: 'Tunisia' }, { code: 'TR', name: 'Turkey' }, { code: 'TM', name: 'Turkmenistan' },
    { code: 'TV', name: 'Tuvalu' }, { code: 'UG', name: 'Uganda' }, { code: 'UA', name: 'Ukraine' },
    { code: 'AE', name: 'United Arab Emirates' }, { code: 'GB', name: 'United Kingdom' },
    { code: 'US', name: 'United States' }, { code: 'UY', name: 'Uruguay' }, { code: 'UZ', name: 'Uzbekistan' },
    { code: 'VU', name: 'Vanuatu' }, { code: 'VE', name: 'Venezuela' }, { code: 'VN', name: 'Vietnam' },
    { code: 'YE', name: 'Yemen' }, { code: 'ZM', name: 'Zambia' }, { code: 'ZW', name: 'Zimbabwe' },
];

// ── Country Multi-Select Component ─────────────────────────────────────────────
interface CountryMultiSelectProps {
    value: string[];
    onChange: (codes: string[]) => void;
    error?: string;
}

const CountryMultiSelect: React.FC<CountryMultiSelectProps> = ({ value, onChange, error }) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);
    const isAllCountries = value.length === 0;
    const filtered = WORLD_COUNTRIES.filter(c =>
        c.name.toLowerCase().includes(search.toLowerCase()) || c.code.toLowerCase().includes(search.toLowerCase())
    );
    const toggleCountry = (code: string) => {
        onChange(value.includes(code) ? value.filter(c => c !== code) : [...value, code]);
    };
    const selectAll = () => { onChange([]); setOpen(false); };
    const removeCountry = (code: string, e: React.MouseEvent) => { e.stopPropagation(); onChange(value.filter(c => c !== code)); };
    useEffect(() => {
        const handler = (e: MouseEvent) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);
    return (
        <div className="relative" ref={dropdownRef}>
            <div className={`w-full min-h-[46px] px-3 py-2 border rounded-lg cursor-pointer flex flex-wrap gap-1 items-center focus-within:ring-2 focus-within:ring-primary-500 transition-colors ${error ? 'border-red-300' : 'border-gray-300'}`} onClick={() => setOpen(!open)}>
                {isAllCountries ? (
                    <span className="flex items-center gap-2 text-green-700 font-medium"><Globe size={14} className="text-green-600" />All Countries (Global)</span>
                ) : (
                    <>{value.map(code => {
                        const ct = WORLD_COUNTRIES.find(c => c.code === code); return (
                            <span key={code} className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-800 text-xs rounded-full">{ct?.name || code}<button type="button" onClick={(e) => removeCountry(code, e)}><X size={10} /></button></span>
                        );
                    })}</>
                )}
                <ChevronDown size={16} className={`ml-auto text-gray-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
            </div>
            {open && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-72 flex flex-col">
                    <div className="p-2 border-b border-gray-100">
                        <div className="relative"><Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input type="text" placeholder="Search country..." value={search} onChange={e => setSearch(e.target.value)} className="w-full pl-7 pr-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-400" onClick={e => e.stopPropagation()} autoFocus />
                        </div>
                    </div>
                    <div className="overflow-y-auto flex-1">
                        <button type="button" onClick={selectAll} className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-green-50 ${isAllCountries ? 'bg-green-50 font-semibold text-green-700' : 'text-gray-700'}`}>
                            <Globe size={14} className="text-green-600 flex-shrink-0" />All Countries (Global){isAllCountries && <span className="ml-auto text-green-600 text-xs">✓ Selected</span>}
                        </button>
                        <div className="border-t border-gray-100 mx-2" />
                        {filtered.map(country => {
                            const selected = value.includes(country.code); return (
                                <button key={country.code} type="button" onClick={() => toggleCountry(country.code)} className={`w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-purple-50 ${selected ? 'bg-purple-50 text-purple-800' : 'text-gray-700'}`}>
                                    <span>{country.name}</span><div className="flex items-center gap-2"><span className="text-xs text-gray-400">{country.code}</span>{selected && <span className="text-purple-600 text-xs">✓</span>}</div>
                                </button>
                            );
                        })}
                        {filtered.length === 0 && <p className="px-3 py-4 text-center text-sm text-gray-500">No countries found</p>}
                    </div>
                    {value.length > 0 && (<div className="p-2 border-t border-gray-100 bg-gray-50 text-xs text-gray-500 flex justify-between items-center"><span>{value.length} countr{value.length === 1 ? 'y' : 'ies'} selected</span><button type="button" onClick={selectAll} className="text-purple-600 hover:underline">Clear all</button></div>)}
                </div>
            )}
            {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
        </div>
    );
};

const CTAValidationSchema = Yup.object({
    title: Yup.string().required("Title is required"),
    description: Yup.string().required("Description is required"),
    button_text: Yup.string().required("Button text is required"),
    href: Yup.string().url("Must be a valid URL").required("Link is required"),
    file_type: Yup.string().required("File type is required"),
});

interface FormValues {
    title: string;
    description: string;
    button_text: string;
    href: string;
    file_type: 'image' | 'video';
    target_countries: string[]; // empty = All Countries (global)
}

interface ImageItem {
    id: string;
    url: string;
    file?: File;
    isNew: boolean;
    name: string;
}

export const CTAEdit: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams<{ id: string }>();

    // Image state management
    const [mainImages, setMainImages] = useState<ImageItem[]>([]);
    const [thumbnailImages, setThumbnailImages] = useState<ImageItem[]>([]);
    const [removedImages, setRemovedImages] = useState<string[]>([]);

    // Determine the base path for navigation
    const basePath = location.pathname.startsWith('/admin') ? '/admin' : '/dashboard';

    // API calls
    const { data: ctaListData, isLoading, isError, error } = useGetCTAPopupsQuery();
    const [updateCTAPopup, { isLoading: isUpdating }] = useUpdateCTAPopupMutation();

    // Find the specific CTA by ID with better error handling
    const ctaData = React.useMemo(() => {
        if (!ctaListData?.data || !Array.isArray(ctaListData.data) || !id) return null;

        const ctaId = Number(id);
        if (isNaN(ctaId)) {
            console.error('Invalid CTA ID:', id);
            return null;
        }

        const found = ctaListData.data.find((cta: CTA) => cta.popup_id === ctaId);
        if (!found) {
            console.error('CTA not found with ID:', ctaId, 'Available CTAs:', ctaListData.data.map(c => c.popup_id));
        }
        return found;
    }, [ctaListData?.data, id]);

    // Initialize existing images when CTA data loads
    useEffect(() => {
        if (ctaData) {
            const existingMainImages: ImageItem[] = [];
            const existingThumbnailImages: ImageItem[] = [];

            // Handle main file
            if (ctaData.file) {
                existingMainImages.push({
                    id: `existing-main-${Date.now()}`,
                    url: ctaData.file,
                    isNew: false,
                    name: ctaData.file.split('/').pop() || 'main-file'
                });
            }

            // Handle thumbnail image
            if (ctaData.thumbnail_image) {
                existingThumbnailImages.push({
                    id: `existing-thumbnail-${Date.now()}`,
                    url: ctaData.thumbnail_image,
                    isNew: false,
                    name: ctaData.thumbnail_image.split('/').pop() || 'thumbnail'
                });
            }

            setMainImages(existingMainImages);
            setThumbnailImages(existingThumbnailImages);
        }
    }, [ctaData]);

    const formik = useFormik<FormValues>({
        initialValues: {
            title: ctaData?.title || "",
            description: ctaData?.description || "",
            button_text: ctaData?.button_text || "",
            href: ctaData?.href || "",
            file_type: ctaData?.file_type || "image",
            target_countries: Array.isArray(ctaData?.target_countries) && ctaData.target_countries.length > 0
                ? ctaData.target_countries
                : [],
        },
        enableReinitialize: true,
        validationSchema: CTAValidationSchema,
        onSubmit: async (values, { resetForm }) => {
            try {
                if (!id) {
                    toast.error("Ad ID is missing");
                    return;
                }

                const formData = new FormData();

                // Add form fields
                formData.append("title", values.title);
                formData.append("description", values.description);
                formData.append("button_text", values.button_text);
                formData.append("href", values.href);
                formData.append("file_type", values.file_type);
                formData.append("popup_id", id);

                // target_countries: empty array = global (null on backend)
                formData.append(
                    "target_countries",
                    values.target_countries.length > 0 ? JSON.stringify(values.target_countries) : 'null'
                );

                // Handle main files - only send new files if they exist
                const newMainFiles = mainImages.filter(img => img.isNew && img.file);
                if (newMainFiles.length > 0) {
                    newMainFiles.forEach((img) => {
                        if (img.file) {
                            formData.append("file", img.file);
                        }
                    });
                }

                // Handle thumbnail files - only send new files if they exist
                const newThumbnailFiles = thumbnailImages.filter(img => img.isNew && img.file);
                if (newThumbnailFiles.length > 0) {
                    newThumbnailFiles.forEach((img) => {
                        if (img.file) {
                            formData.append("thumbnail_image", img.file);
                        }
                    });
                }

                await updateCTAPopup(formData).unwrap();
                toast.success("Ad updated successfully!");
                resetForm();
                setMainImages([]);
                setThumbnailImages([]);
                setRemovedImages([]);
                navigate(`${basePath}/cta-management`);
            } catch (error) {
                console.error('Failed to update Ad:', error);
                toast.error('Failed to update Ad. Please try again.');
            }
        },
    });

    const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>, field: 'main' | 'thumbnail') => {
        const files = event.target.files;
        if (!files || files.length === 0) return;

        const maxSize = formik.values.file_type === "image" ? 5 * 1024 * 1024 : 50 * 1024 * 1024;
        const maxFiles = field === 'main' ? 5 : 3; // Allow more main files, fewer thumbnails

        const newImages: ImageItem[] = [];

        for (let i = 0; i < Math.min(files.length, maxFiles); i++) {
            const file = files[i];

            // Validate file size
            if (file.size > maxSize) {
                toast.error(`File ${file.name} is too large. Max size: ${formik.values.file_type === "image" ? "5MB" : "50MB"}`);
                continue;
            }

            // Validate file type
            if (field === "main") {
                if (formik.values.file_type === "image" && !file.type.startsWith("image/")) {
                    toast.error(`File ${file.name} is not a valid image file`);
                    continue;
                }
                if (formik.values.file_type === "video" && !file.type.startsWith("video/")) {
                    toast.error(`File ${file.name} is not a valid video file`);
                    continue;
                }
            } else if (field === "thumbnail") {
                if (!file.type.startsWith("image/")) {
                    toast.error(`File ${file.name} is not a valid image file for thumbnail`);
                    continue;
                }
            }

            const imageItem: ImageItem = {
                id: `new-${field}-${Date.now()}-${i}`,
                url: URL.createObjectURL(file),
                file: file,
                isNew: true,
                name: file.name
            };

            newImages.push(imageItem);
        }

        if (field === 'main') {
            // Replace existing main images with new ones
            setMainImages(prev => {
                // Clean up old blob URLs
                prev.forEach(img => {
                    if (img.isNew && img.url.startsWith('blob:')) {
                        URL.revokeObjectURL(img.url);
                    }
                });
                return newImages;
            });
            toast.success(`${newImages.length} new ${formik.values.file_type === "image" ? "image(s)" : "video(s)"} will replace existing ones!`);
        } else {
            // Replace existing thumbnail images with new ones
            setThumbnailImages(prev => {
                // Clean up old blob URLs
                prev.forEach(img => {
                    if (img.isNew && img.url.startsWith('blob:')) {
                        URL.revokeObjectURL(img.url);
                    }
                });
                return newImages;
            });
            toast.success(`${newImages.length} new thumbnail(s) will replace existing ones!`);
        }

        // Clear the input
        event.target.value = '';
    }, [formik.values.file_type]);

    const handleImageRemove = useCallback((imageId: string, field: 'main' | 'thumbnail') => {
        if (field === 'main') {
            const imageToRemove = mainImages.find(img => img.id === imageId);
            if (imageToRemove) {
                if (imageToRemove.isNew) {
                    // Remove new image completely
                    setMainImages(prev => prev.filter(img => img.id !== imageId));
                    if (imageToRemove.url.startsWith('blob:')) {
                        URL.revokeObjectURL(imageToRemove.url);
                    }
                } else {
                    // Mark existing image for removal
                    setRemovedImages(prev => [...prev, imageId]);
                }
                toast.success("Image removed successfully!");
            }
        } else {
            const imageToRemove = thumbnailImages.find(img => img.id === imageId);
            if (imageToRemove) {
                if (imageToRemove.isNew) {
                    // Remove new image completely
                    setThumbnailImages(prev => prev.filter(img => img.id !== imageId));
                    if (imageToRemove.url.startsWith('blob:')) {
                        URL.revokeObjectURL(imageToRemove.url);
                    }
                } else {
                    // Mark existing image for removal
                    setRemovedImages(prev => [...prev, imageId]);
                }
                toast.success("Thumbnail removed successfully!");
            }
        }
    }, [mainImages, thumbnailImages]);

    const handleFileTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newType = e.target.value as 'image' | 'video';
        formik.setFieldValue("file_type", newType);

        // Clear all images when type changes
        setMainImages([]);
        setThumbnailImages([]);
        setRemovedImages([]);
    };

    const getFieldError = (fieldName: keyof FormValues) => {
        return formik.touched[fieldName] && formik.errors[fieldName] ? formik.errors[fieldName] : "";
    };

    // Cleanup preview URLs on unmount
    useEffect(() => {
        return () => {
            mainImages.forEach(img => {
                if (img.isNew && img.url.startsWith('blob:')) {
                    URL.revokeObjectURL(img.url);
                }
            });
            thumbnailImages.forEach(img => {
                if (img.isNew && img.url.startsWith('blob:')) {
                    URL.revokeObjectURL(img.url);
                }
            });
        };
    }, [mainImages, thumbnailImages]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading Ad...</p>
                </div>
            </div>
        );
    }

    if (isError) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="text-center">
                    <div className="text-red-500 text-6xl mb-4">⚠️</div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">Error Loading Ad</h3>
                    <p className="text-gray-600 mb-4">
                        {error ? (error as any)?.data?.message || "Please try again later" : "Please try again later"}
                    </p>
                    <Button
                        variant="outline"
                        onClick={() => navigate(`${basePath}/cta-management`)}
                    >
                        Back to App Ads Slider
                    </Button>
                </div>
            </div>
        );
    }

    if (!ctaData) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="text-center">
                    <div className="text-gray-400 text-6xl mb-4">🔍</div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">Ad Not Found</h3>
                    <p className="text-gray-600 mb-4">
                        {!id ? "No Ad ID provided" :
                            isNaN(Number(id)) ? "Invalid Ad ID format" :
                                "The ad you're looking for doesn't exist"}
                    </p>
                    <Button
                        variant="outline"
                        onClick={() => navigate("/admin/cta-management")}
                        className="mt-4"
                    >
                        Back to App Ads Slider
                    </Button>
                </div>
            </div>
        );
    }

    const renderImagePreview = (image: ImageItem, field: 'main' | 'thumbnail') => {
        const isRemoved = removedImages.includes(image.id);

        return (
            <div
                key={image.id}
                className={`relative group border rounded-lg overflow-hidden ${isRemoved ? 'opacity-50' : ''
                    }`}
            >
                {formik.values.file_type === "image" || field === 'thumbnail' ? (
                    <img
                        src={image.url}
                        alt={image.name}
                        className="w-full h-32 object-cover"
                    />
                ) : (
                    <video
                        src={image.url}
                        className="w-full h-32 object-cover"
                        muted
                    />
                )}

                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleImageRemove(image.id, field)}
                            className="text-white bg-red-600 hover:bg-red-700"
                        >
                            <Trash2 size={16} />
                        </Button>
                    </div>
                </div>

                <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-75 text-white text-xs p-2">
                    <p className="truncate">{image.name}</p>
                    {image.isNew && <span className="text-green-400">New</span>}
                    {isRemoved && <span className="text-red-400">Removed</span>}
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-6">
            {/* Header Section */}
            <PageHeader
                title="Edit App Ads Slider"
                description="Update app ads slider details"
            >
                <Button
                    variant="outline"
                    onClick={() => navigate(`${basePath}/cta-management`)}
                    className="flex items-center gap-2"
                >
                    <ArrowLeft size={20} />
                    Back to Ads
                </Button>
            </PageHeader>

            {/* Form Section */}
            <Card>
                <form onSubmit={formik.handleSubmit} className="space-y-6 p-8">
                    {/* Basic Information */}
                    <div className="border-b border-gray-200 pb-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                                    Title <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="title"
                                    name="title"
                                    placeholder="Enter ad title"
                                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors ${getFieldError("title")
                                        ? "border-red-300 focus:border-red-500"
                                        : "border-gray-300 focus:border-primary-500"
                                        }`}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.title}
                                />
                                {getFieldError("title") && (
                                    <p className="text-red-500 text-xs mt-1">{getFieldError("title")}</p>
                                )}
                            </div>

                            <div>
                                <label htmlFor="file_type" className="block text-sm font-medium text-gray-700 mb-2">
                                    File Type <span className="text-red-500">*</span>
                                </label>
                                <select
                                    id="file_type"
                                    name="file_type"
                                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors ${getFieldError("file_type")
                                        ? "border-red-300 focus:border-red-500"
                                        : "border-gray-300 focus:border-primary-500"
                                        }`}
                                    onChange={handleFileTypeChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.file_type}
                                >
                                    <option value="image">Image</option>
                                    <option value="video">Video</option>
                                </select>
                                {getFieldError("file_type") && (
                                    <p className="text-red-500 text-xs mt-1">{getFieldError("file_type")}</p>
                                )}
                            </div>
                        </div>

                        <div className="mt-6">
                            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                                Description <span className="text-red-500">*</span>
                            </label>
                            <textarea
                                id="description"
                                name="description"
                                placeholder="Enter ad description"
                                rows={4}
                                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors resize-none ${getFieldError("description")
                                    ? "border-red-300 focus:border-red-500"
                                    : "border-gray-300 focus:border-primary-500"
                                    }`}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.description}
                            />
                            {getFieldError("description") && (
                                <p className="text-red-500 text-xs mt-1">{getFieldError("description")}</p>
                            )}
                        </div>
                    </div>

                    {/* Button & Link Configuration */}
                    <div className="border-b border-gray-200 pb-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">Button & Link Configuration</h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label htmlFor="button_text" className="block text-sm font-medium text-gray-700 mb-2">
                                    Button Text <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="button_text"
                                    name="button_text"
                                    placeholder="e.g., Click Here, Learn More, Get Started"
                                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors ${getFieldError("button_text")
                                        ? "border-red-300 focus:border-red-500"
                                        : "border-gray-300 focus:border-primary-500"
                                        }`}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.button_text}
                                />
                                {getFieldError("button_text") && (
                                    <p className="text-red-500 text-xs mt-1">{getFieldError("button_text")}</p>
                                )}
                            </div>

                            <div>
                                <label htmlFor="href" className="block text-sm font-medium text-gray-700 mb-2">
                                    Link URL <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="url"
                                    id="href"
                                    name="href"
                                    placeholder="https://example.com"
                                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors ${getFieldError("href")
                                        ? "border-red-300 focus:border-red-500"
                                        : "border-gray-300 focus:border-primary-500"
                                        }`}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.href}
                                />
                                {getFieldError("href") && (
                                    <p className="text-red-500 text-xs mt-1">{getFieldError("href")}</p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Country Targeting */}
                    <div className="border-b border-gray-200 pb-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2">
                            <Globe size={18} className="text-purple-600" /> Target Countries
                        </h2>
                        <p className="text-sm text-gray-500 mb-4">
                            Select which countries should see this ad. Leave as <strong>All Countries</strong> to show globally.
                        </p>
                        <CountryMultiSelect
                            value={formik.values.target_countries}
                            onChange={(codes) => formik.setFieldValue('target_countries', codes)}
                        />
                        {formik.values.target_countries.length === 0 ? (
                            <p className="text-xs text-green-600 mt-2 flex items-center gap-1"><Globe size={12} /> This ad will be shown globally to all users</p>
                        ) : (
                            <p className="text-xs text-purple-600 mt-2">This ad will only appear to users in the {formik.values.target_countries.length} selected countr{formik.values.target_countries.length === 1 ? 'y' : 'ies'}</p>
                        )}
                    </div>

                    {/* File Upload Section */}
                    <div className="border-b border-gray-200 pb-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">Media Files</h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Main Files Section */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Main {formik.values.file_type === "image" ? "Images" : "Videos"}
                                </label>

                                {/* Existing Images Preview */}
                                {mainImages.length > 0 && (
                                    <div className="mb-4">
                                        <h4 className="text-sm font-medium text-gray-600 mb-2">Current Files</h4>
                                        <div className="grid grid-cols-2 gap-2">
                                            {mainImages.map(image => renderImagePreview(image, 'main'))}
                                        </div>
                                    </div>
                                )}

                                {/* Upload Area */}
                                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary-500 transition-colors">
                                    <input
                                        type="file"
                                        accept={formik.values.file_type === "image" ? "image/*" : "video/*"}
                                        onChange={(e) => handleFileChange(e, "main")}
                                        className="hidden"
                                        id="main-file-upload"
                                        multiple
                                    />
                                    <label htmlFor="main-file-upload" className="cursor-pointer">
                                        {formik.values.file_type === "image" ? (
                                            <ImageIcon className="mx-auto h-12 w-12 text-gray-400" />
                                        ) : (
                                            <Video className="mx-auto h-12 w-12 text-gray-400" />
                                        )}
                                        <p className="mt-2 text-sm text-gray-600">
                                            Replace {formik.values.file_type === "image" ? "Images" : "Videos"}
                                        </p>
                                        <p className="text-xs text-gray-500 mt-1">
                                            {formik.values.file_type === "image"
                                                ? "Supported formats: JPG, PNG, GIF (Max 5MB each)"
                                                : "Supported formats: MP4, AVI, MOV (Max 50MB each)"
                                            }
                                        </p>
                                        <p className="text-xs text-gray-500 mt-1">
                                            New files will replace existing ones
                                        </p>
                                    </label>
                                </div>
                            </div>

                            {/* Thumbnail Section */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Thumbnail Images
                                </label>

                                {/* Existing Thumbnails Preview */}
                                {thumbnailImages.length > 0 && (
                                    <div className="mb-4">
                                        <h4 className="text-sm font-medium text-gray-600 mb-2">Current Thumbnails</h4>
                                        <div className="grid grid-cols-2 gap-2">
                                            {thumbnailImages.map(image => renderImagePreview(image, 'thumbnail'))}
                                        </div>
                                    </div>
                                )}

                                {/* Upload Area */}
                                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary-500 transition-colors">
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => handleFileChange(e, "thumbnail")}
                                        className="hidden"
                                        id="thumbnail-upload"
                                        multiple
                                    />
                                    <label htmlFor="thumbnail-upload" className="cursor-pointer">
                                        <ImageIcon className="mx-auto h-12 w-12 text-gray-400" />
                                        <p className="mt-2 text-sm text-gray-600">Replace Thumbnails</p>
                                        <p className="text-xs text-gray-500 mt-1">
                                            Optional thumbnails for video previews
                                        </p>
                                        <p className="text-xs text-gray-500 mt-1">
                                            New files will replace existing ones
                                        </p>
                                    </label>
                                </div>
                            </div>
                        </div>

                        {/* Summary */}
                        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                            <h4 className="text-sm font-medium text-gray-700 mb-2">Summary</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                <div>
                                    <p className="text-gray-600">
                                        Main {formik.values.file_type === "image" ? "Images" : "Videos"}: {mainImages.filter(img => !removedImages.includes(img.id)).length} active
                                    </p>
                                    <p className="text-gray-600">
                                        Thumbnails: {thumbnailImages.filter(img => !removedImages.includes(img.id)).length} active
                                    </p>
                                </div>
                                <div>
                                    <p className="text-gray-600">
                                        New files: {mainImages.filter(img => img.isNew).length + thumbnailImages.filter(img => img.isNew).length}
                                    </p>
                                    <p className="text-gray-600">
                                        Removed: {removedImages.length}
                                    </p>
                                </div>
                            </div>
                            {mainImages.filter(img => img.isNew).length > 0 || thumbnailImages.filter(img => img.isNew).length > 0 ? (
                                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                    <p className="text-xs text-blue-700">
                                        ⚠️ New files will replace existing ones when you submit
                                    </p>
                                </div>
                            ) : null}
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-4 pt-6">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => navigate(`${basePath}/cta-management`)}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={isUpdating}
                            className="flex items-center gap-2"
                        >
                            <Save size={16} />
                            {isUpdating ? "Updating Ad..." : "Update Ad"}
                        </Button>
                    </div>
                </form>
            </Card>
        </div>
    );
};

export default CTAEdit; 